// Original SVG icon set for the HUD (no Civ assets). 24×24 grid, drawn with currentColor so a
// parent sets the tint. Strokes are round-capped at 1.7 to read at 16–32 px.

import type { JSX } from 'preact';
import type { NotificationKind } from '../services/api.ts';

const STROKE = 1.7;

// Filled shapes use this so they sit under the outline without hiding it.
const SOFT = 0.28;

const ICONS = {
  // ─── Yields ───────────────────────────────────────────────────────────────────────────
  economy: (
    <>
      <ellipse cx="12" cy="6.5" rx="7" ry="3" fill="currentColor" fill-opacity={SOFT} />
      <path d="M5 6.5v4c0 1.7 3.1 3 7 3s7-1.3 7-3v-4" />
      <path d="M5 10.5v4c0 1.7 3.1 3 7 3s7-1.3 7-3v-4" />
      <path d="M5 14.5v3c0 1.7 3.1 3 7 3s7-1.3 7-3v-3" />
    </>
  ),
  science: (
    <>
      <path d="M7 15h10l2.4 4a.9.9 0 0 1-.8 1.4H5.4a.9.9 0 0 1-.8-1.4z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M8.5 3h7M10 3v6L4.6 18.7a1.4 1.4 0 0 0 1.2 2.1h12.4a1.4 1.4 0 0 0 1.2-2.1L14 9V3" />
      <circle cx="11" cy="17.2" r=".9" fill="currentColor" />
      <circle cx="14" cy="15.8" r=".6" fill="currentColor" />
    </>
  ),
  culture: (
    <>
      <path d="M8 16c-3.4-2.2-4.2-7.8-1.4-12M16 16c3.4-2.2 4.2-7.8 1.4-12" />
      <path d="M5.4 6.2h13.2" />
      <path d="M10 6.2V16M12 6.2V16M14 6.2V16" stroke-width="1.1" />
      <path d="M7.5 16h9l-1 3.6a1.5 1.5 0 0 1-1.4 1.1H9.9a1.5 1.5 0 0 1-1.4-1.1z" fill="currentColor" fill-opacity={SOFT} />
    </>
  ),
  power: (
    <>
      <path d="M12 2.6l7.2 2.6v6c0 5-3.3 8.6-7.2 10.4C8.1 19.8 4.8 16.2 4.8 11.2v-6z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M12 6.2v10.2M8.8 13.2h6.4" />
      <path d="M10.6 16.4h2.8" />
    </>
  ),
  diplomacy: (
    <>
      <path d="M4.5 20.5C8 16 12.5 10.5 19.5 4" />
      <path d="M8.2 16.3c-2.4.4-4.2-.6-4.9-2.6 2.4-.5 4.2.5 4.9 2.6z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M8.4 16.1c-.6-2.4.2-4.3 2.2-5.2.7 2.4-.2 4.3-2.2 5.2z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M12 12.2c-2.4.2-4-.9-4.6-2.9 2.4-.3 4 .8 4.6 2.9z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M12.2 12c-.4-2.4.5-4.2 2.6-5 .5 2.4-.5 4.2-2.6 5z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M15.6 8.3c-2.2 0-3.6-1.2-4-3.2 2.3-.1 3.7 1.1 4 3.2z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M16 8.1c-.1-2.3 1-3.9 3.1-4.4.2 2.3-1 3.9-3.1 4.4z" fill="currentColor" fill-opacity={SOFT} />
    </>
  ),
  population: (
    <>
      <circle cx="9" cy="8" r="3.2" fill="currentColor" fill-opacity={SOFT} />
      <path d="M3 20.2c0-3.6 2.7-6.2 6-6.2s6 2.6 6 6.2" />
      <circle cx="16.6" cy="8.8" r="2.6" />
      <path d="M15.6 14.1c3 .3 5.4 2.5 5.4 6.1" />
    </>
  ),

  // ─── Notification kinds ───────────────────────────────────────────────────────────────
  event: (
    <>
      <path d="M6 3.5h10.5A2.5 2.5 0 0 1 19 6v14.5H7.5A2.5 2.5 0 0 1 5 18V4.5a1 1 0 0 1 1-1z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M8.5 8h7M8.5 11.5h7M8.5 15h4.5" />
    </>
  ),
  war: (
    <>
      <path d="M4 4l11 11M13 17.2l4.2-4.2M15.3 15.3l4 4" />
      <path d="M20 4L9 15M6.8 13l4.2 4.2M8.7 15.3l-4 4" />
      <path d="M4 4l3.2.8L4.8 7.2zM20 4l-3.2.8 2.4 2.4z" fill="currentColor" />
    </>
  ),
  control: (
    <>
      <path d="M6.5 21V3.5" />
      <path d="M6.5 4h11l-2.6 3.6 2.6 3.6h-11" fill="currentColor" fill-opacity={SOFT} />
      <path d="M3 21h9" />
    </>
  ),
  border: (
    <>
      <path d="M12 2.8l8 4.6v9.2l-8 4.6-8-4.6V7.4z" stroke-dasharray="3.2 2.2" />
      <circle cx="12" cy="12" r="2.2" fill="currentColor" />
    </>
  ),
  membership: (
    <>
      <circle cx="9" cy="12" r="5.2" />
      <circle cx="15" cy="12" r="5.2" />
      <path d="M12 7.8a5.2 5.2 0 0 1 0 8.4 5.2 5.2 0 0 1 0-8.4z" fill="currentColor" fill-opacity={0.5} />
    </>
  ),
  leader: (
    <>
      <path d="M4.6 16.8L3.6 7.5l4.8 3.8L12 5l3.6 6.3 4.8-3.8-1 9.3z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M4.6 19.5h14.8" />
      <circle cx="3.6" cy="7.5" r=".9" fill="currentColor" />
      <circle cx="12" cy="5" r=".9" fill="currentColor" />
      <circle cx="20.4" cy="7.5" r=".9" fill="currentColor" />
    </>
  ),
  milestone: (
    <>
      <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" fill="currentColor" fill-opacity={SOFT} />
    </>
  ),
  wonder: (
    <>
      <path d="M3 9.2L12 4l9 5.2z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M5.2 9.5v8.3M9.7 9.5v8.3M14.3 9.5v8.3M18.8 9.5v8.3" />
      <path d="M3.5 18h17M2.5 20.8h19" />
    </>
  ),
  disaster: (
    <>
      <path d="M12 3.2L2.6 20h18.8z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M13.2 8.5l-3 4.8h3.6l-2.8 4.6" />
    </>
  ),
  launch: (
    <>
      <path d="M12 2.5c3 2.2 4.4 5.4 4.4 9.4v4.3H7.6v-4.3c0-4 1.4-7.2 4.4-9.4z" fill="currentColor" fill-opacity={SOFT} />
      <circle cx="12" cy="9.6" r="1.6" />
      <path d="M7.6 12.6l-3 3.1v3.2l3-1.6M16.4 12.6l3 3.1v3.2l-3-1.6" />
      <path d="M10 18.8l2 2.8 2-2.8" />
    </>
  ),

  // ─── Lenses ───────────────────────────────────────────────────────────────────────────
  political: (
    <>
      <path d="M6 3v18" />
      <path d="M6 4.2h12.5l-3 4 3 4H6" fill="currentColor" fill-opacity={SOFT} />
    </>
  ),
  alliance: (
    <>
      <path d="M9 3.5l5.5 2v4.6c0 3.9-2.5 6.7-5.5 8.1-3-1.4-5.5-4.2-5.5-8.1V5.5z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M15 5.8l5.5 2v4.6c0 3.9-2.5 6.7-5.5 8.1-1.2-.6-2.3-1.4-3.2-2.4" />
    </>
  ),
  freshness: (
    <>
      <path d="M6 3h12M6 21h12" />
      <path d="M7.2 3c0 5 4.8 6 4.8 9s-4.8 4-4.8 9M16.8 3c0 5-4.8 6-4.8 9s4.8 4 4.8 9" />
      <path d="M9 19.5c1-1.8 2-2.4 3-2.4s2 .6 3 2.4z" fill="currentColor" />
    </>
  ),
  lens: (
    <>
      <circle cx="10.5" cy="10.5" r="6.2" fill="currentColor" fill-opacity={SOFT} />
      <path d="M15.2 15.2L20.5 20.5" stroke-width="2.4" />
    </>
  ),
  religion: (
    <>
      <path d="M12 3.5l2.3 5.1 5.5.5-4.2 3.7 1.3 5.5L12 15.4l-4.9 2.9 1.3-5.5-4.2-3.7 5.5-.5z" />
    </>
  ),

  // ─── Relations and grievances ─────────────────────────────────────────────────────────
  ally: (
    <>
      <path d="M12 2.8l7.2 2.6v6c0 5-3.3 8.6-7.2 10.4C8.1 20 4.8 16.4 4.8 11.4v-6z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M8.4 12l2.6 2.6 4.8-5.2" />
    </>
  ),
  sanction: (
    <>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M6.2 6.2l11.6 11.6" stroke-width="2.2" />
    </>
  ),
  tariff: (
    <>
      <path d="M3.5 12.2l8.7-8.7h8.3v8.3l-8.7 8.7z" fill="currentColor" fill-opacity={SOFT} />
      <circle cx="16.4" cy="7.6" r="1.4" />
      <path d="M8.5 14l5-5" />
    </>
  ),
  friend: (
    <>
      <path d="M12 20s-7.5-4.6-7.5-10.1A4.2 4.2 0 0 1 12 7.4a4.2 4.2 0 0 1 7.5 2.5C19.5 15.4 12 20 12 20z" fill="currentColor" fill-opacity={SOFT} />
    </>
  ),
  neutral: (
    <>
      <circle cx="12" cy="12" r="7.5" />
      <path d="M8 12h8" />
    </>
  ),
  denounced: (
    <>
      <path d="M12 3.2L2.6 20h18.8z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M12 9v5.5M12 17v.4" stroke-width="2.2" />
    </>
  ),

  // ─── Chrome ───────────────────────────────────────────────────────────────────────────
  source: (
    <>
      <path d="M3.8 5.6C6.4 4 9.4 4 12 5.6 14.6 4 17.6 4 20.2 5.6v13.6c-2.6-1.5-5.6-1.5-8.2 0-2.6-1.5-5.6-1.5-8.2 0z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M12 5.6v13.6" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 10.8v6M12 7.4v.4" stroke-width="2.2" />
    </>
  ),
  turn: (
    <>
      <path d="M4.5 12a7.5 7.5 0 0 1 13-5.1M19.5 12a7.5 7.5 0 0 1-13 5.1" />
      <path d="M17.8 3.5v3.8H14M6.2 20.5v-3.8H10" />
    </>
  ),
  next: (
    <>
      <path d="M4 12h13" stroke-width="2.4" />
      <path d="M12.5 5.5L19 12l-6.5 6.5" stroke-width="2.4" />
    </>
  ),
  play: <path d="M8 4.8l11.2 7.2L8 19.2z" fill="currentColor" />,
  pause: <path d="M7.2 5h3.4v14H7.2zM13.4 5h3.4v14h-3.4z" fill="currentColor" stroke="none" />,
  speed: (
    <>
      <path d="M3.5 6.5l8 5.5-8 5.5zM12 6.5l8 5.5-8 5.5z" fill="currentColor" fill-opacity={SOFT} />
    </>
  ),
  close: <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" stroke-width="2" />,
  log: (
    <>
      <path d="M9 6.5h11M9 12h11M9 17.5h11" />
      <circle cx="4.8" cy="6.5" r="1.2" fill="currentColor" />
      <circle cx="4.8" cy="12" r="1.2" fill="currentColor" />
      <circle cx="4.8" cy="17.5" r="1.2" fill="currentColor" />
    </>
  ),
  victory: (
    <>
      <path d="M7 3.8h10v5.4a5 5 0 0 1-10 0z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M7 5.8H4.2a3 3 0 0 0 3.2 4.2M17 5.8h2.8a3 3 0 0 1-3.2 4.2" />
      <path d="M12 14.2v3.6M8.2 21h7.6M9.6 17.8h4.8" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <ellipse cx="12" cy="12" rx="3.8" ry="8.6" />
      <path d="M3.4 12h17.2M5 7.5h14M5 16.5h14" stroke-width="1.1" />
    </>
  ),
  map: (
    <>
      <path d="M3 6.3l6-2.2 6 2.2 6-2.2v13.6l-6 2.2-6-2.2-6 2.2z" fill="currentColor" fill-opacity={SOFT} />
      <path d="M9 4.1v13.6M15 6.3v13.6" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="6.5" />
      <circle cx="12" cy="12" r="1.8" fill="currentColor" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
    </>
  ),
  external: (
    <>
      <path d="M14 4h6v6M20 4l-8.5 8.5" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </>
  ),
  chevronDown: <path d="M6 9.5l6 6 6-6" stroke-width="2" />,
  chevronUp: <path d="M6 14.5l6-6 6 6" stroke-width="2" />,
  chevronLeft: <path d="M14.5 6l-6 6 6 6" stroke-width="2" />,
  chevronRight: <path d="M9.5 6l6 6-6 6" stroke-width="2" />,
  star: <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" fill="currentColor" />,
  scales: (
    <>
      <path d="M12 3.5v17M7 20.5h10M5 6.5h14" />
      <path d="M5 6.5l-2.6 6.2a2.8 2.8 0 0 0 5.2 0zM19 6.5l-2.6 6.2a2.8 2.8 0 0 0 5.2 0z" fill="currentColor" fill-opacity={SOFT} />
    </>
  ),
  quote: (
    <>
      <path d="M4 17.5c0-4.2 1.4-7.6 5-9.8l.9 1.4C7.8 10.6 7 12.2 7 14h3v6H4zM14 17.5c0-4.2 1.4-7.6 5-9.8l.9 1.4C17.8 10.6 17 12.2 17 14h3v6h-6z" fill="currentColor" stroke="none" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="1.6" fill="currentColor" fill-opacity={SOFT} />
      <path d="M3.5 9.5h17M8 3v4M16 3v4" />
    </>
  ),
  // A non-state party: three figures, the middle one leading. (A pennant read as the letter F.)
  faction: (
    <>
      <circle cx="12" cy="8" r="2.5" />
      <path d="M7.4 19.6v-1.4a4.6 4.6 0 0 1 9.2 0v1.4" />
      <circle cx="4.8" cy="10.4" r="1.8" />
      <path d="M1.8 19.6v-.6a3.2 3.2 0 0 1 3-3.2" />
      <circle cx="19.2" cy="10.4" r="1.8" />
      <path d="M22.2 19.6v-.6a3.2 3.2 0 0 0-3-3.2" />
    </>
  ),
  mock: (
    <>
      <path d="M4 6h16v12H4z" stroke-dasharray="2.6 1.8" />
      <path d="M8 10l2 2-2 2M12 14h4" />
    </>
  ),
} satisfies Record<string, JSX.Element>;

export type IconName = keyof typeof ICONS;

/** Icon for each notification kind. */
export const KIND_ICON: Record<NotificationKind, IconName> = {
  event: 'event',
  war: 'war',
  control: 'control',
  border: 'border',
  membership: 'membership',
  leader: 'leader',
  milestone: 'milestone',
  wonder: 'wonder',
  disaster: 'disaster',
  launch: 'launch',
};

/** Lens id → icon; unknown lenses get the magnifier. */
export function lensIcon(id: string): IconName {
  const known: Record<string, IconName> = {
    political: 'political',
    alliance: 'alliance',
    alliances: 'alliance',
    conflict: 'war',
    war: 'war',
    economy: 'economy',
    economic: 'economy',
    freshness: 'freshness',
    staleness: 'freshness',
    religion: 'religion',
    population: 'population',
    resources: 'economy',
    science: 'science',
    culture: 'culture',
  };
  return known[id] ?? 'lens';
}

interface IconProps {
  name: IconName;
  size?: number;
  class?: string;
  title?: string;
}

export function Icon({ name, size = 20, class: cls, title }: IconProps) {
  return (
    <svg
      class={cls ? `wc-icon ${cls}` : 'wc-icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width={STROKE}
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden={title ? undefined : 'true'}
      role={title ? 'img' : undefined}
    >
      {title ? <title>{title}</title> : null}
      {ICONS[name]}
    </svg>
  );
}
